import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp,
  Timestamp,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from './config';
import { User, CheckIn, Location, WorkLocation, CalendarEvent, CalendarEventFormData, PTOFormula, PTOFormulaFormData } from '../types';

// User operations
export const getUser = async (userId: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return { 
        id: userDoc.id, 
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
        dateOfBirth: data.dateOfBirth instanceof Timestamp ? data.dateOfBirth.toDate() : data.dateOfBirth,
        dateOfHire: data.dateOfHire instanceof Timestamp ? data.dateOfHire.toDate() : data.dateOfHire
      } as User;
    }
    return null;
  } catch (error) {
    console.error('Error getting user:', error);
    throw error;
  }
};

export const createUser = async (userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>) => {
  try {
    const userRef = doc(db, 'users', userData.email);
    await setDoc(userRef, {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return userRef.id;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

// New function: Get or create user
export const getOrCreateUser = async (authUser: { uid: string; email: string | null; displayName: string | null }): Promise<User> => {
  try {
    // First, try to get existing user
    const existingUser = await getUser(authUser.uid);
    
    if (existingUser) {
      return existingUser;
    }

    // If user doesn't exist, create a new one with default values
    const newUserData = {
      email: authUser.email || '',
      name: authUser.displayName || 'New User',
      workLocation: {
        latitude: 0, // Default coordinates (will need to be updated)
        longitude: 0,
        address: 'Work location not set',
        name: 'Default Location'
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Create user document with UID as document ID
    await setDoc(doc(db, 'users', authUser.uid), newUserData);
    
    // Return the created user with current timestamp
    return {
      id: authUser.uid,
      email: newUserData.email,
      name: newUserData.name,
      workLocation: newUserData.workLocation,
      createdAt: new Date(),
      updatedAt: new Date()
    } as User;
  } catch (error) {
    console.error('Error getting or creating user:', error);
    throw error;
  }
};

// Check-in operations
export const createCheckIn = async (
  userId: string,
  location: Location,
  distance: number,
  workLocationId?: string,
  workLocationName?: string
): Promise<string> => {
  try {
    const checkInData = {
      userId,
      timestamp: serverTimestamp(),
      location,
      distance,
      workLocationId: workLocationId || null,
      workLocationName: workLocationName || null
    };
    
    const docRef = await addDoc(collection(db, 'check-ins'), checkInData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating check-in:', error);
    throw error;
  }
};

export const getUserCheckIns = async (userId: string): Promise<CheckIn[]> => {
  try {
    const q = query(
      collection(db, 'check-ins'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const checkIns: CheckIn[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      checkIns.push({ 
        id: doc.id, 
        ...data,
        timestamp: data.timestamp instanceof Timestamp ? data.timestamp.toDate() : data.timestamp
      } as CheckIn);
    });
    
    return checkIns;
  } catch (error) {
    console.error('Error getting user check-ins:', error);
    throw error;
  }
};

export const getLatestCheckIn = async (userId: string): Promise<CheckIn | null> => {
  try {
    const q = query(
      collection(db, 'check-ins'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      return { 
        id: doc.id, 
        ...data,
        timestamp: data.timestamp instanceof Timestamp ? data.timestamp.toDate() : data.timestamp
      } as CheckIn;
    }
    return null;
  } catch (error) {
    console.error('Error getting latest check-in:', error);
    throw error;
  }
};

// Admin functions
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'));
    const users: User[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      users.push({ 
        id: doc.id, 
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
        dateOfBirth: data.dateOfBirth instanceof Timestamp ? data.dateOfBirth.toDate() : data.dateOfBirth,
        dateOfHire: data.dateOfHire instanceof Timestamp ? data.dateOfHire.toDate() : data.dateOfHire
      } as User);
    });
    
    return users;
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

export const getAllCheckIns = async (): Promise<CheckIn[]> => {
  try {
    const q = query(
      collection(db, 'check-ins'),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const checkIns: CheckIn[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      checkIns.push({ 
        id: doc.id, 
        ...data,
        timestamp: data.timestamp instanceof Timestamp ? data.timestamp.toDate() : data.timestamp
      } as CheckIn);
    });
    
    return checkIns;
  } catch (error) {
    console.error('Error getting all check-ins:', error);
    throw error;
  }
};

export const updateUserWorkLocation = async (userId: string, workLocation: Location): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, { workLocation, updatedAt: serverTimestamp() }, { merge: true });
  } catch (error) {
    console.error('Error updating user work location:', error);
    throw error;
  }
};

export const createUserDocument = async (
  userId: string,
  userData: {
    name: string;
    email: string;
    workLocation: Location;
    pto?: number;
    dateOfBirth?: string;
    dateOfHire?: string;
  }
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    
    // Convert date strings to Date objects if provided
    const userDocData: any = {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    if (userData.dateOfBirth) {
      // Create date in local timezone to avoid timezone shift issues
      const [year, month, day] = userData.dateOfBirth.split('-').map(Number);
      userDocData.dateOfBirth = new Date(year, month - 1, day);
    }
    
    if (userData.dateOfHire) {
      // Create date in local timezone to avoid timezone shift issues
      const [year, month, day] = userData.dateOfHire.split('-').map(Number);
      userDocData.dateOfHire = new Date(year, month - 1, day);
    }
    
    await setDoc(userRef, userDocData);
  } catch (error) {
    console.error('Error creating user document:', error);
    throw error;
  }
};

// New function to create user document without authentication
export const createUserDocumentAsAdmin = async (uid: string, userData: Omit<User, 'uid'>): Promise<void> => {
  const userRef = doc(db, 'users', uid);
  await setDoc(userRef, {
    ...userData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// Work Location operations
export const createWorkLocation = async (workLocationData: Omit<WorkLocation, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const workLocationRef = await addDoc(collection(db, 'locations'), {
      ...workLocationData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return workLocationRef.id;
  } catch (error) {
    console.error('Error creating work location:', error);
    throw error;
  }
};

export const getAllWorkLocations = async (): Promise<WorkLocation[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'locations'));
    const workLocations: WorkLocation[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Ensure latitude and longitude are numbers
      const latitude = typeof data.latitude === 'number' ? data.latitude : 0;
      const longitude = typeof data.longitude === 'number' ? data.longitude : 0;
      
      workLocations.push({ 
        id: doc.id, 
        name: data.name || '',
        address: data.address || '',
        latitude,
        longitude,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
      } as WorkLocation);
    });
    
    return workLocations;
  } catch (error) {
    console.error('Error getting all work locations:', error);
    throw error;
  }
};

export const updateWorkLocation = async (locationId: string, workLocationData: Partial<Omit<WorkLocation, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> => {
  try {
    await updateDoc(doc(db, 'locations', locationId), {
      ...workLocationData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating work location:', error);
    throw error;
  }
};

export const deleteWorkLocation = async (locationId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'locations', locationId));
  } catch (error) {
    console.error('Error deleting work location:', error);
    throw error;
  }
};

export const getWorkLocation = async (locationId: string): Promise<WorkLocation | null> => {
  try {
    const locationDoc = await getDoc(doc(db, 'locations', locationId));
    if (locationDoc.exists()) {
      const data = locationDoc.data();
      
      // Ensure latitude and longitude are numbers
      const latitude = typeof data.latitude === 'number' ? data.latitude : 0;
      const longitude = typeof data.longitude === 'number' ? data.longitude : 0;
      
      return { 
        id: locationDoc.id, 
        name: data.name || '',
        address: data.address || '',
        latitude,
        longitude,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
      } as WorkLocation;
    }
    return null;
  } catch (error) {
    console.error('Error getting work location:', error);
    throw error;
  }
};

export const assignWorkLocationToUser = async (userId: string, workLocationId: string): Promise<void> => {
  try {
    // Get the work location details
    const workLocation = await getWorkLocation(workLocationId);
    if (!workLocation) {
      throw new Error('Work location not found');
    }

    // Update user's work location
    await updateDoc(doc(db, 'users', userId), {
      workLocation: {
        latitude: workLocation.latitude,
        longitude: workLocation.longitude,
        address: workLocation.address,
        name: workLocation.name
      },
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error assigning work location to user:', error);
    throw error;
  }
};

// IP-based authentication functions
export const checkIPConflict = async (currentUserId: string, ipAddress: string): Promise<boolean> => {
  try {
    // Get all users except the current user
    const q = query(
      collection(db, 'users'),
      where('lastLoginIp', '==', ipAddress)
    );
    
    const querySnapshot = await getDocs(q);
    
    // Check each user's IP timestamp
    for (const doc of querySnapshot.docs) {
      const userData = doc.data();
      const userId = doc.id;
      
      // Skip the current user
      if (userId === currentUserId) {
        continue;
      }
      
      // Check if IP is less than 12 hours old
      if (userData.lastLoginTime) {
        const lastLoginTime = userData.lastLoginTime instanceof Timestamp 
          ? userData.lastLoginTime.toDate() 
          : new Date(userData.lastLoginTime);
        
        const hoursSinceLogin = (Date.now() - lastLoginTime.getTime()) / (1000 * 60 * 60);
        
        if (hoursSinceLogin < 12) {
          // IP conflict found - another user has used this IP within 12 hours
          return true;
        }
      }
    }
    
    return false; // No conflict found
  } catch (error) {
    console.error('Error checking IP conflict:', error);
    throw error;
  }
};

export const updateUserIP = async (userId: string, ipAddress: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      lastLoginIp: ipAddress,
      lastLoginTime: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user IP:', error);
    throw error;
  }
};

// Helper function to calculate number of days between two dates (inclusive)
const calculateDaysBetween = (startDate: Date, endDate: Date): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays + 1; // +1 to include both start and end dates
};

// Calendar Event operations
export const createCalendarEvent = async (
  eventData: CalendarEventFormData,
  createdBy: string
): Promise<string> => {
  try {
    // Create dates in local timezone to avoid timezone issues
    const startDate = new Date(eventData.startDate + 'T00:00:00');
    const endDate = new Date(eventData.endDate + 'T23:59:59');
    
    const calendarEventData = {
      ...eventData,
      startDate: startDate,
      endDate: endDate,
      createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'calendar-events'), calendarEventData);
    
    // If this is an izin event for a specific user, decrease their PTO
    if (eventData.type === 'izin' && eventData.scope === 'user_specific' && eventData.userId) {
      try {
        console.log('Processing PTO deduction for izin event:', {
          userId: eventData.userId,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
        
        const user = await getUser(eventData.userId);
        if (!user) {
          console.error('User not found for PTO update:', eventData.userId);
          return docRef.id;
        }
        
        const daysToDeduct = calculateDaysBetween(startDate, endDate);
        const currentPTO = user.pto || 0;
        const newPTO = Math.max(0, currentPTO - daysToDeduct);
        
        console.log('PTO update details:', {
          userId: eventData.userId,
          currentPTO,
          daysToDeduct,
          newPTO
        });
        
        await updateUserPTO(eventData.userId, newPTO);
        console.log('PTO successfully updated to:', newPTO);
      } catch (ptoError) {
        console.error('Error updating user PTO after creating izin event:', ptoError);
        // Don't throw - event was created successfully, PTO update can be fixed manually
      }
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating calendar event:', error);
    throw error;
  }
};

export const getCalendarEvents = async (
  startDate?: Date,
  endDate?: Date,
  userId?: string
): Promise<CalendarEvent[]> => {
  try {
    let q = query(collection(db, 'calendar-events'), orderBy('startDate', 'asc'));
    
    // Filter by date range if provided
    if (startDate && endDate) {
      q = query(
        collection(db, 'calendar-events'),
        where('startDate', '>=', startDate),
        where('startDate', '<=', endDate),
        orderBy('startDate', 'asc')
      );
    }
    
    const querySnapshot = await getDocs(q);
    const events: CalendarEvent[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const event = { 
        id: doc.id, 
        ...data,
        startDate: data.startDate instanceof Timestamp ? data.startDate.toDate() : data.startDate,
        endDate: data.endDate instanceof Timestamp ? data.endDate.toDate() : data.endDate,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
      } as CalendarEvent;
      
      // Filter by user if specified
      if (userId) {
        if (event.scope === 'company_wide' || event.userId === userId) {
          events.push(event);
        }
      } else {
        events.push(event);
      }
    });
    
    return events;
  } catch (error) {
    console.error('Error getting calendar events:', error);
    throw error;
  }
};

export const getCalendarEvent = async (eventId: string): Promise<CalendarEvent | null> => {
  try {
    const eventDoc = await getDoc(doc(db, 'calendar-events', eventId));
    if (eventDoc.exists()) {
      const data = eventDoc.data();
      return { 
        id: eventDoc.id, 
        ...data,
        startDate: data.startDate instanceof Timestamp ? data.startDate.toDate() : data.startDate,
        endDate: data.endDate instanceof Timestamp ? data.endDate.toDate() : data.endDate,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
      } as CalendarEvent;
    }
    return null;
  } catch (error) {
    console.error('Error getting calendar event:', error);
    throw error;
  }
};

export const updateCalendarEvent = async (
  eventId: string,
  eventData: Partial<CalendarEventFormData>
): Promise<void> => {
  try {
    // Get the existing event to handle PTO adjustments
    const existingEvent = await getCalendarEvent(eventId);
    
    const updateData: any = {
      updatedAt: serverTimestamp()
    };
    
    // Only include fields that are actually provided and not undefined
    if (eventData.type !== undefined) updateData.type = eventData.type;
    if (eventData.title !== undefined) updateData.title = eventData.title;
    if (eventData.description !== undefined) updateData.description = eventData.description;
    if (eventData.isAllDay !== undefined) updateData.isAllDay = eventData.isAllDay;
    if (eventData.scope !== undefined) updateData.scope = eventData.scope;
    
    // Handle userId - only include if it's not undefined
    if (eventData.userId !== undefined) {
      updateData.userId = eventData.userId;
    } else if (eventData.scope === 'company_wide') {
      // For company-wide events, explicitly remove userId field
      updateData.userId = null;
    }
    
    // Convert date strings to Date objects if provided
    let newStartDate = existingEvent?.startDate;
    let newEndDate = existingEvent?.endDate;
    if (eventData.startDate) {
      newStartDate = new Date(eventData.startDate + 'T00:00:00');
      updateData.startDate = newStartDate;
    }
    if (eventData.endDate) {
      newEndDate = new Date(eventData.endDate + 'T23:59:59');
      updateData.endDate = newEndDate;
    }
    
    await updateDoc(doc(db, 'calendar-events', eventId), updateData);
    
    // Handle PTO adjustments for izin events
    if (existingEvent && newStartDate && newEndDate) {
      const finalType = eventData.type ?? existingEvent.type;
      const finalScope = eventData.scope ?? existingEvent.scope;
      const finalUserId = (eventData.userId !== undefined) ? eventData.userId : existingEvent.userId;
      
      const wasIzin = existingEvent.type === 'izin' && existingEvent.scope === 'user_specific' && existingEvent.userId;
      const isIzin = finalType === 'izin' && finalScope === 'user_specific' && finalUserId;
      
      const oldUserId = existingEvent.userId;
      const newUserId = finalUserId;
      
      // If it was an izin event, restore the PTO for the old user
      if (wasIzin && oldUserId) {
        try {
          const oldDays = calculateDaysBetween(existingEvent.startDate, existingEvent.endDate);
          const user = await getUser(oldUserId);
          if (user) {
            const currentPTO = user.pto || 0;
            await updateUserPTO(oldUserId, currentPTO + oldDays);
            console.log('Restored PTO for user:', oldUserId, 'added', oldDays, 'days');
          }
        } catch (ptoError) {
          console.error('Error restoring user PTO after updating event:', ptoError);
        }
      }
      
      // If it's now an izin event, deduct the PTO for the new user
      if (isIzin && newUserId) {
        try {
          const newDays = calculateDaysBetween(newStartDate, newEndDate);
          const user = await getUser(newUserId);
          if (user) {
            const currentPTO = user.pto || 0;
            const newPTO = Math.max(0, currentPTO - newDays);
            await updateUserPTO(newUserId, newPTO);
            console.log('Deducted PTO for user:', newUserId, 'deducted', newDays, 'days, new PTO:', newPTO);
          }
        } catch (ptoError) {
          console.error('Error updating user PTO after updating event:', ptoError);
        }
      }
    }
  } catch (error) {
    console.error('Error updating calendar event:', error);
    throw error;
  }
};

export const deleteCalendarEvent = async (eventId: string): Promise<void> => {
  try {
    // Get the event before deleting to restore PTO if it's an izin event
    const event = await getCalendarEvent(eventId);
    
    // If this is an izin event for a specific user, restore their PTO
    if (event && event.type === 'izin' && event.scope === 'user_specific' && event.userId) {
      try {
        const user = await getUser(event.userId);
        if (user) {
          const daysToRestore = calculateDaysBetween(event.startDate, event.endDate);
          const currentPTO = user.pto || 0;
          await updateUserPTO(event.userId, currentPTO + daysToRestore);
          console.log('Restored PTO for user:', event.userId, 'added', daysToRestore, 'days');
        }
      } catch (ptoError) {
        console.error('Error restoring user PTO after deleting izin event:', ptoError);
        // Don't throw - continue with deletion
      }
    }
    
    await deleteDoc(doc(db, 'calendar-events', eventId));
  } catch (error) {
    console.error('Error deleting calendar event:', error);
    throw error;
  }
};

// Get events for a specific month
export const getMonthCalendarEvents = async (year: number, month: number): Promise<CalendarEvent[]> => {
  try {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0, 23, 59, 59);
    
    return await getCalendarEvents(startDate, endDate);
  } catch (error) {
    console.error('Error getting month calendar events:', error);
    throw error;
  }
};

// User disable/enable functionality
export const toggleUserDisabledStatus = async (userId: string, disabled: boolean): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      disabled,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user disabled status:', error);
    throw error;
  }
};

// PTO functionality
export const updateUserPTO = async (userId: string, pto: number): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      pto,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user PTO:', error);
    throw error;
  }
};

// Date functionality
export const updateUserDateOfBirth = async (userId: string, dateOfBirth: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    const updateData: any = {
      updatedAt: serverTimestamp()
    };
    
    if (dateOfBirth) {
      // Create date in local timezone to avoid timezone shift issues
      const [year, month, day] = dateOfBirth.split('-').map(Number);
      updateData.dateOfBirth = new Date(year, month - 1, day);
    } else {
      updateData.dateOfBirth = null;
    }
    
    await updateDoc(userRef, updateData);
  } catch (error) {
    console.error('Error updating user date of birth:', error);
    throw error;
  }
};

export const updateUserDateOfHire = async (userId: string, dateOfHire: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    const updateData: any = {
      updatedAt: serverTimestamp()
    };
    
    if (dateOfHire) {
      // Create date in local timezone to avoid timezone shift issues
      const [year, month, day] = dateOfHire.split('-').map(Number);
      updateData.dateOfHire = new Date(year, month - 1, day);
    } else {
      updateData.dateOfHire = null;
    }
    
    await updateDoc(userRef, updateData);
  } catch (error) {
    console.error('Error updating user date of hire:', error);
    throw error;
  }
};

// PTO Formula operations
export const createPTOFormula = async (
  formulaData: PTOFormulaFormData,
  createdBy: string
): Promise<string> => {
  try {
    // Clean up undefined values - Firestore doesn't allow undefined
    const cleanedData: any = {
      name: formulaData.name,
      description: formulaData.description,
      minYearsOfService: formulaData.minYearsOfService,
      ptoDays: formulaData.ptoDays,
      isActive: formulaData.isActive,
      createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Only add optional fields if they have values
    if (formulaData.maxYearsOfService !== undefined) {
      cleanedData.maxYearsOfService = formulaData.maxYearsOfService;
    }
    if (formulaData.minAge !== undefined) {
      cleanedData.minAge = formulaData.minAge;
    }
    if (formulaData.maxAge !== undefined) {
      cleanedData.maxAge = formulaData.maxAge;
    }
    
    const docRef = await addDoc(collection(db, 'pto-formulas'), cleanedData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating PTO formula:', error);
    throw error;
  }
};

export const getAllPTOFormulas = async (): Promise<PTOFormula[]> => {
  try {
    const q = query(
      collection(db, 'pto-formulas'),
      orderBy('minYearsOfService', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const formulas: PTOFormula[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      formulas.push({ 
        id: doc.id, 
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
      } as PTOFormula);
    });
    
    return formulas;
  } catch (error) {
    console.error('Error getting all PTO formulas:', error);
    throw error;
  }
};

export const getPTOFormula = async (formulaId: string): Promise<PTOFormula | null> => {
  try {
    const formulaDoc = await getDoc(doc(db, 'pto-formulas', formulaId));
    if (formulaDoc.exists()) {
      const data = formulaDoc.data();
      return { 
        id: formulaDoc.id, 
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
      } as PTOFormula;
    }
    return null;
  } catch (error) {
    console.error('Error getting PTO formula:', error);
    throw error;
  }
};

export const updatePTOFormula = async (
  formulaId: string,
  formulaData: Partial<PTOFormulaFormData>
): Promise<void> => {
  try {
    const updateData: any = {
      updatedAt: serverTimestamp()
    };
    
    // Only include fields that are actually provided and not undefined
    if (formulaData.name !== undefined) updateData.name = formulaData.name;
    if (formulaData.description !== undefined) updateData.description = formulaData.description;
    if (formulaData.minYearsOfService !== undefined) updateData.minYearsOfService = formulaData.minYearsOfService;
    if (formulaData.ptoDays !== undefined) updateData.ptoDays = formulaData.ptoDays;
    if (formulaData.isActive !== undefined) updateData.isActive = formulaData.isActive;
    
    // Handle optional fields - only include if they have values
    if (formulaData.maxYearsOfService !== undefined) {
      updateData.maxYearsOfService = formulaData.maxYearsOfService;
    }
    if (formulaData.minAge !== undefined) {
      updateData.minAge = formulaData.minAge;
    }
    if (formulaData.maxAge !== undefined) {
      updateData.maxAge = formulaData.maxAge;
    }
    
    await updateDoc(doc(db, 'pto-formulas', formulaId), updateData);
  } catch (error) {
    console.error('Error updating PTO formula:', error);
    throw error;
  }
};

export const deletePTOFormula = async (formulaId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'pto-formulas', formulaId));
  } catch (error) {
    console.error('Error deleting PTO formula:', error);
    throw error;
  }
};

// PTO Calculation logic
export const calculateUserPTO = async (userId: string): Promise<number> => {
  try {
    const user = await getUser(userId);
    if (!user || !user.dateOfHire || !user.dateOfBirth) {
      return user?.pto || 0; // Return current PTO or default
    }

    const formulas = await getAllPTOFormulas();
    const activeFormulas = formulas.filter(f => f.isActive);
    
    if (activeFormulas.length === 0) {
      return user.pto || 0; // Return current PTO or default if no formulas
    }

    const today = new Date();
    const hireDate = user.dateOfHire;
    const birthDate = user.dateOfBirth;
    
    // Calculate years of service
    const yearsOfService = today.getFullYear() - hireDate.getFullYear();
    const monthDiff = today.getMonth() - hireDate.getMonth();
    const actualYearsOfService = monthDiff < 0 ? yearsOfService - 1 : yearsOfService;
    
    // Calculate age
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthAgeDiff = today.getMonth() - birthDate.getMonth();
    const actualAge = monthAgeDiff < 0 ? age - 1 : age;

    // Find all matching formulas and select the one with highest PTO days
    let maxPtoDays = 0;
    let hasMatchingFormula = false;
    
    for (const formula of activeFormulas) {
      const matchesYearsOfService = actualYearsOfService >= formula.minYearsOfService && 
        (formula.maxYearsOfService === undefined || actualYearsOfService <= formula.maxYearsOfService);
      
      const matchesAge = (formula.minAge === undefined || actualAge >= formula.minAge) &&
        (formula.maxAge === undefined || actualAge <= formula.maxAge);
      
      if (matchesYearsOfService && matchesAge) {
        hasMatchingFormula = true;
        if (formula.ptoDays > maxPtoDays) {
          maxPtoDays = formula.ptoDays;
        }
      }
    }
    
    if (hasMatchingFormula) {
      return maxPtoDays;
    }

    // If no formula matches, return current PTO or default
    return user.pto || 0;
  } catch (error) {
    console.error('Error calculating user PTO:', error);
    throw error;
  }
};

// Bulk PTO calculation for all users
export const calculateAllUsersPTO = async (): Promise<void> => {
  try {
    // Read users directly to access raw fields (including accrual dates)
    const querySnapshot = await getDocs(collection(db, 'users'));

    const processPromises = querySnapshot.docs.map(async (userDoc) => {
      const data: any = userDoc.data();

      const userId = userDoc.id;
      const pto: number = typeof data.pto === 'number' ? data.pto : 0;
      const dateOfHire: Date | null = data.dateOfHire instanceof Timestamp
        ? data.dateOfHire.toDate()
        : data.dateOfHire
          ? new Date(data.dateOfHire)
          : null;
      const dateOfBirth: Date | null = data.dateOfBirth instanceof Timestamp
        ? data.dateOfBirth.toDate()
        : data.dateOfBirth
          ? new Date(data.dateOfBirth)
          : null;
      let lastPtoAccrualDate: Date | null = data.lastPtoAccrualDate instanceof Timestamp
        ? data.lastPtoAccrualDate.toDate()
        : data.lastPtoAccrualDate
          ? new Date(data.lastPtoAccrualDate)
          : null;
      let nextPtoAccrualDate: Date | null = data.nextPtoAccrualDate instanceof Timestamp
        ? data.nextPtoAccrualDate.toDate()
        : data.nextPtoAccrualDate
          ? new Date(data.nextPtoAccrualDate)
          : null;

      // If required fields are missing, skip accrual processing
      if (!dateOfHire || !dateOfBirth) {
        return; // cannot compute annual PTO without hire date and birth date
      }

      const today = new Date();

      // Initialize next accrual date if missing: set to the next upcoming anniversary
      if (!nextPtoAccrualDate) {
        const thisYearAnniversary = new Date(
          today.getFullYear(),
          dateOfHire.getMonth(),
          dateOfHire.getDate()
        );
        nextPtoAccrualDate = thisYearAnniversary > today
          ? thisYearAnniversary
          : new Date(today.getFullYear() + 1, dateOfHire.getMonth(), dateOfHire.getDate());

        await updateDoc(doc(db, 'users', userId), {
          lastPtoAccrualDate: lastPtoAccrualDate || null,
          nextPtoAccrualDate,
          updatedAt: serverTimestamp()
        });
        // Do not accrue in the same pass; next run will handle if due
        return;
      }

      // If accrual is due (today >= nextPtoAccrualDate), add-on to existing PTO
      if (today >= nextPtoAccrualDate) {
        let currentPto = pto;
        let workingNextAccrual = new Date(nextPtoAccrualDate);
        let workingLastAccrual = lastPtoAccrualDate ? new Date(lastPtoAccrualDate) : null;

        // Catch up for multiple missed anniversaries
        while (workingNextAccrual <= today) {
          const annualPto = await calculateUserPTO(userId);
          currentPto += annualPto;
          workingLastAccrual = new Date(workingNextAccrual);
          // Move to next year anniversary
          workingNextAccrual = new Date(
            workingNextAccrual.getFullYear() + 1,
            workingNextAccrual.getMonth(),
            workingNextAccrual.getDate()
          );
        }

        await updateDoc(doc(db, 'users', userId), {
          pto: currentPto,
          lastPtoAccrualDate: workingLastAccrual,
          nextPtoAccrualDate: workingNextAccrual,
          updatedAt: serverTimestamp()
        });
      }
      // If not due, do nothing
    });

    await Promise.all(processPromises);
  } catch (error) {
    console.error('Error calculating all users PTO:', error);
    throw error;
  }
};

// Fix all users' nextPtoAccrualDate to be correct based on current date
export const fixAllUsersAccrualDates = async (): Promise<void> => {
  try {
    const users = await getAllUsers();
    const today = new Date();

    const updatePromises = users.map(async (user) => {
      if (user.dateOfHire) {
        const hireDate = user.dateOfHire;

        // Calculate correct next anniversary date
        const thisYearAnniversary = new Date(
          today.getFullYear(),
          hireDate.getMonth(),
          hireDate.getDate()
        );

        const nextAccrualDate = thisYearAnniversary > today
          ? thisYearAnniversary
          : new Date(today.getFullYear() + 1, hireDate.getMonth(), hireDate.getDate());

        await updateDoc(doc(db, 'users', user.id), {
          nextPtoAccrualDate: nextAccrualDate,
          updatedAt: serverTimestamp()
        });
      }
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error fixing all users accrual dates:', error);
    throw error;
  }
};